import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconButton, Stack } from '@mui/material';

import { Context, PostWrapper } from './scroller.models';
import { EType } from '@backend/models';
import { LikeButton } from '../../components/like-button/like-button';
import moment from 'moment/moment';
import {
  IoChatboxEllipsesOutline,
  IoDownloadOutline,
  IoShareSocialOutline,
} from 'react-icons/io5';
import { IoTrashOutline } from 'react-icons/io5';
import QuickPinchZoom, { make3dTransformValue } from 'react-quick-pinch-zoom';

interface InnerItemProps {
  index: number;
  postWrapper: PostWrapper;
  context: Context;
  noTitle?: boolean;
}

// Item contents are cached properly with React.memo
export const InnerItem = React.memo((props: InnerItemProps) => {
  const post = props.postWrapper.post.data;

  let title = '';
  if (post.challenge?.id && !props.noTitle) {
    const c = props.context.challenges.find((x) => x.id == post.challenge?.id);
    if (c) {
      title = c.text;
    }
  }
  const [transformed, setTransformed] = useState(false);

  const imgRef = useRef<HTMLImageElement>(null);

  const onUpdate = useCallback(
    ({ x, y, scale }: any) => {
      const { current: img } = imgRef;

      if (img) {
        const value = make3dTransformValue({ x, y, scale });
        if (scale == 1) {
          if (!transformed) {
            setTransformed(false);
          }
        }
        img.style.setProperty('transform', value);
      }
    },
    [transformed]
  );

  return (
    <>
      {post.type === EType.Video && (
        <div
          className={'video-c'}
          onClick={(evt) => {
            console.log(evt);
            const video = evt.target as HTMLVideoElement;
            if (!video) {
              return;
            }
            const src = video.getAttribute('src');
            const dataSrc = video.getAttribute('data-src');
            if (dataSrc && !src) {
              video.setAttribute('src', dataSrc);
              try {
                video.muted = false;
                video.play();
              } catch (e: any) {
                //nothing
              }
            }
          }}
        >
          <video
            data-src={post.images?.video}
            autoPlay={true}
            controls
            poster={post.images?.i640w}
          />
        </div>
      )}
      {post.type === EType.Image && (
        <>
          <QuickPinchZoom
            onUpdate={onUpdate}
            onZoomStart={() => {
              if (!transformed) {
                setTransformed(true);
              }
            }}
            onDragEnd={() => {
              setTimeout(() => {
                setTransformed(false);
              }, 500);
            }}
          >
            <img ref={imgRef} src={post.images?.i640w} />
          </QuickPinchZoom>

          <div
            className={'myDragger myDragger-t' + (transformed ? ' kk' : '')}
          ></div>
          <div
            className={'myDragger myDragger-r' + (transformed ? ' kk' : '')}
          ></div>
          <div
            className={'myDragger myDragger-b' + (transformed ? ' kk' : '')}
          ></div>
          <div
            className={'myDragger myDragger-l' + (transformed ? ' kk' : '')}
          ></div>
        </>
      )}

      <Stack className={'scroller--buttons'}>
        <Stack className={'scroller--buttons-inner'} direction={'column'}>
          <LikeButton
            liked={post.selfLiked}
            likes={post.likes}
            asStack={true}
            stackDirection={'column'}
            setLiked={(val) => {
              props.context.like(props.postWrapper, val);
            }}
          />

          <IconButton
            sx={{ color: '#FFFFFF' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              props.context.comment(props.postWrapper);
            }}
          >
            <Stack>
              <IoChatboxEllipsesOutline />
              <div className={'counter'}>
                {props.postWrapper.post.data.commentsCount > 0
                  ? props.postWrapper.post.data.commentsCount
                  : ''}
              </div>
            </Stack>
          </IconButton>

          <IconButton
            sx={{ color: '#FFFFFF' }}
            onClick={() => props.context.share(props.postWrapper)}
          >
            <IoShareSocialOutline />
          </IconButton>
          <IconButton
            sx={{ color: '#FFFFFF' }}
            href={
              post.type === EType.Video
                ? post.images?.video || ''
                : post.images?.download || ''
            }
            target={'_blank'}
          >
            <IoDownloadOutline />
          </IconButton>
          {post.editable && (
            <IconButton
              sx={{ color: '#FFFFFF' }}
              onClick={() => props.context.delete(props.postWrapper)}
            >
              <IoTrashOutline />
            </IconButton>
          )}
        </Stack>
      </Stack>
      <div
        className={
          'scroller--footer ' +
          (post.type === EType.Video ? ' scroller--footer-video' : '')
        }
      >
        {!!title && <div className={'title'}>{title}</div>}
        <h5>
          von <strong>{post.userName}</strong>
        </h5>
        <h6>
          {moment(post.createdDate.substring(0, 19) + 'Z').format(
            'DD.MM.YYYY, HH:mm'
          )}
        </h6>
      </div>
    </>
  );
});

// The callback is executed often - don't inline complex components in here.
export const itemContent = (
  index: number,
  post: PostWrapper,
  context: Context
) => {
  return <InnerItem index={index} postWrapper={post} context={context} />;
};
